import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Fragment } from 'react';

import CheckIcon from '~/shared/assets/icons/check.svg';
import Typography from '~/shared/components/Typography';

const transitionProps = {
  enter: 'transition ease-out duration-100',
  enterFrom: 'transform opacity-0 scale-95',
  enterTo: 'transform opacity-100 scale-100',
  leave: 'transition ease-in duration-75',
  leaveFrom: 'transform opacity-100 scale-100',
  leaveTo: 'transform opacity-0 scale-95',
};

interface MultiSelectDropdownProps {
  items: string[];
  selectedItems: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  align?: 'top-right' | 'top-left';
  className?: string;
  menuButtonClassName?: string;
  menuItemClassName?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

export default function MultiSelectDropdown({
  items,
  selectedItems,
  onChange,
  placeholder = 'Select',
  align = 'top-right',
  className,
  menuButtonClassName,
  menuItemClassName,
  disabled = false,
  'aria-label': ariaLabel,
}: MultiSelectDropdownProps) {
  const orderedSelectedItems = items.filter((item) => selectedItems.includes(item));
  const selectedLabel = orderedSelectedItems.length > 0 ? orderedSelectedItems.join(', ') : placeholder;

  const toggleItem = (item: string) => {
    if (selectedItems.includes(item)) {
      onChange(selectedItems.filter((v) => v !== item));
      return;
    }
    onChange([...selectedItems, item]);
  };

  return (
    <div className={clsx('relative', className)}>
      <Popover className="relative flex">
        {({ open }) => (
          <>
            <PopoverButton
              disabled={disabled}
              aria-label={ariaLabel}
              className={clsx(
                'bg-white text-primary-900 border border-gray-300 flex justify-between rounded-[8px] px-2 py-3 h-9 2xl:h-11 w-full',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                !disabled && open
                  ? 'bg-primary-20 border-primary-200'
                  : !disabled && 'hover:border-primary-500 active:border-primary-100',
                'focus:border-primary-300 focus:outline-none',
                menuButtonClassName
              )}
            >
              <div className="flex items-center justify-between w-full gap-2">
                <Typography variant="p3-reg" color={selectedItems.length > 0 ? 'text-gray-700' : 'text-gray-500'}>
                  {selectedLabel}
                </Typography>

                <ChevronDownIcon
                  className={clsx(
                    'w-[14px] h-[14px] text-gray-400 stroke-3 cursor-pointer shrink-0',
                    open ? 'rotate-180' : ''
                  )}
                />
              </div>
            </PopoverButton>

            <Transition as={Fragment} {...transitionProps}>
              <PopoverPanel
                className={clsx(
                  'absolute top-[100%] rounded-lg mt-2 origin-top-right w-full bg-white',
                  'shadow-filter-dropdown focus-outline-none border-primary-70',
                  'border border-primary-70 focus:outline-hidden z-30',
                  align === 'top-right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left'
                )}
              >
                <div className="p3">
                  {items.map((item) => {
                    const isSelected = selectedItems.includes(item);
                    return (
                      <button
                        key={item}
                        type="button"
                        role="menuitemcheckbox"
                        aria-checked={isSelected}
                        className={clsx(
                          'text-sm leading-6 w-full h-12 flex items-center justify-between px-5 py-[18px] border-gray-200 cursor-pointer hover:bg-primary-20 group',
                          items[0] === item && 'rounded-t-lg',
                          item !== items[items.length - 1] && 'border-b border-gray-200',
                          menuItemClassName
                        )}
                        onClick={() => toggleItem(item)}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            aria-hidden="true"
                            className={clsx(
                              'size-5 p-[4px] rounded-md border border-primary-100 flex items-center justify-center',
                              isSelected ? 'bg-primary-500' : 'bg-white'
                            )}
                          >
                            <CheckIcon
                              className={clsx(
                                'text-white stroke-2 size-2.5 transition duration-200 ease-in-out',
                                isSelected ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                          </div>
                          <Typography color="text-primary-900" variant="p3-reg">
                            {item}
                          </Typography>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
}
